import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, centAusText, errorText, formatDate, formatDateTime, formatGeld } from '../api';
import { useAuth } from '../auth';
import { KundenAuswahl } from '../components/KundenAuswahl';
import { KundeKasten, PositionenEditor, VorgangAktionen, VorgangKopf, VorgangVerlauf, leerePosition, positionenFuerApi, type PositionEingabe } from '../components/Vorgang';
import { Badge, Card, Dialog, Empty, Field, Loading, Notice, kundenName } from '../components/ui';
import type { Forderung, Kunde, Vorgang, Zahlung } from '../types';

const ANSICHTEN = [
  { schluessel: 'offen', label: 'Alle offenen' },
  { schluessel: 'beleg_fehlt', label: 'Beleg fehlt' },
  { schluessel: 'zahlung_offen', label: 'Zahlung offen' },
  { schluessel: 'ueberfaellig', label: 'Überfällig' },
  { schluessel: 'abgeschlossen', label: 'Erledigt' },
] as const;

const ZAHLART: Record<Zahlung['art'], string> = { bar: 'bar', ec: 'EC-Karte', ueberweisung: 'Überweisung', sonstiges: 'sonstige' };

const herkunft = (f: Forderung) =>
  f.lieferung_nummer ?? f.serviceauftrag_nummer ?? f.bestellung_nummer ?? (f.einsatz_nummer ? `Einsatz ${f.einsatz_nummer}` : 'von Hand');

/**
 * Offene Forderungen. Die Druckansicht ist für den händischen Abgleich mit
 * den offenen Posten in MehrData gedacht - eine Schnittstelle gibt es nicht.
 */
export function ForderungenPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const ansicht = params.get('ansicht') ?? 'offen';
  const [q, setQ] = useState('');
  const [liste, setListe] = useState<Forderung[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [neu, setNeu] = useState(false);

  useEffect(() => {
    setListe(null);
    const zeit = window.setTimeout(() => {
      api<Forderung[]>('/api/forderungen', { query: { ansicht, q } })
        .then(setListe)
        .catch((e) => setFehler(errorText(e)));
    }, 200);
    return () => window.clearTimeout(zeit);
  }, [ansicht, q]);

  const summeOffen = (liste ?? []).reduce((s, f) => s + Math.max(0, f.betrag_cent - f.bezahlt_cent), 0);

  return (
    <>
      <div className="kopf nicht-drucken">
        <h1>Forderungen</h1>
        <span className="spacer" />
        <button onClick={() => window.print()}>🖨 Liste für MehrData-Abgleich</button>
        <button className="primary" onClick={() => setNeu(true)}>
          + Forderung
        </button>
      </div>
      <div className="tabs nicht-drucken">
        {ANSICHTEN.map((a) => (
          <button key={a.schluessel} className={a.schluessel === ansicht ? 'aktiv' : ''} onClick={() => setParams({ ansicht: a.schluessel }, { replace: true })}>
            {a.label}
          </button>
        ))}
      </div>
      <input className="nicht-drucken" placeholder="Nummer, Beleg, Kunde, Vorgang …" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 14 }} />
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <h2 className="nur-drucken">
        Forderungen ({ANSICHTEN.find((a) => a.schluessel === ansicht)?.label}) – Stand {formatDateTime(new Date().toISOString())}
      </h2>
      <Card padded={false}>
        {!liste ? (
          <Loading />
        ) : liste.length === 0 ? (
          <Empty>Keine Forderungen in dieser Ansicht.</Empty>
        ) : (
          <div className="tabelle-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nr.</th>
                  <th>Kunde</th>
                  <th>Herkunft</th>
                  <th>MehrData-Beleg</th>
                  <th style={{ textAlign: 'right' }}>Betrag</th>
                  <th style={{ textAlign: 'right' }}>Offen</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((f) => (
                  <tr key={f.id} className="klickbar" onClick={() => navigate(`/forderungen/${f.id}`)}>
                    <td className="mono">{f.nummer}</td>
                    <td>
                      {kundenName({ vorname: f.kunde_vorname, nachname: f.kunde_nachname, firma: f.kunde_firma })}
                      <div className="small muted">{formatDate(f.angelegt_am)}</div>
                    </td>
                    <td className="small">{herkunft(f)}</td>
                    <td className="mono small">{f.mehrdata_beleg ?? <Badge kind="warn">fehlt</Badge>}</td>
                    <td style={{ textAlign: 'right' }}>{formatGeld(f.betrag_cent)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{formatGeld(Math.max(0, f.betrag_cent - f.bezahlt_cent))}</strong>
                    </td>
                    <td>
                      <Badge kind={f.status === 'abgeschlossen' ? 'ok' : f.ueberfaellig ? 'bad' : f.status === 'storniert' ? 'default' : 'info'}>
                        {f.status === 'abgeschlossen' ? 'erledigt' : f.status === 'storniert' ? 'storniert' : f.status_bezeichnung}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {ansicht !== 'abgeschlossen' && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right' }}>
                      <strong>Summe offen</strong>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{formatGeld(summeOffen)}</strong>
                    </td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {neu && <NeueForderung onClose={() => setNeu(false)} onAngelegt={(id) => navigate(`/forderungen/${id}`)} />}
    </>
  );
}

function NeueForderung({ onClose, onAngelegt }: { onClose: () => void; onAngelegt: (id: string) => void }) {
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [positionen, setPositionen] = useState<PositionEingabe[]>([leerePosition()]);
  const [beleg, setBeleg] = useState('');
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState('');
  const pos = positionenFuerApi(positionen).filter((p) => (p.preisCent ?? 0) > 0);

  async function anlegen() {
    try {
      const f = await api<Forderung>('/api/forderungen', {
        method: 'POST',
        body: { kundeId: kunde!.id, positionen: pos.map((p) => ({ bezeichnung: p.bezeichnung, menge: p.menge, preisCent: p.preisCent })), beleg: beleg || null, notiz: notiz || null },
      });
      onAngelegt(f.id);
    } catch (e) {
      setFehler(errorText(e));
    }
  }

  return (
    <Dialog
      titel="Neue Forderung"
      breit
      onClose={onClose}
      aktionen={
        <>
          <button onClick={onClose}>Abbrechen</button>
          <button className="primary" disabled={!kunde || pos.length === 0} onClick={() => void anlegen()}>
            Anlegen
          </button>
        </>
      }
    >
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <KundenAuswahl gewaehlt={kunde} onWahl={setKunde} />
      <PositionenEditor positionen={positionen} onChange={setPositionen} />
      <div className="grid2">
        <Field label="MehrData-Beleg (falls schon geschrieben)">
          <input value={beleg} onChange={(e) => setBeleg(e.target.value)} />
        </Field>
        <Field label="Notiz">
          <input value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

export function ForderungDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const [f, setF] = useState<Forderung | null>(null);
  const [fehler, setFehler] = useState('');
  const [dialog, setDialog] = useState<'beleg' | 'zahlung' | { storno: Zahlung } | null>(null);
  const [beleg, setBeleg] = useState('');
  const [betrag, setBetrag] = useState('');
  const [art, setArt] = useState<Zahlung['art']>('ueberweisung');
  const [datum, setDatum] = useState('');
  const [text, setText] = useState('');

  const laden = useCallback(() => {
    api<Forderung>(`/api/forderungen/${id}`)
      .then(setF)
      .catch((e) => setFehler(errorText(e)));
  }, [id]);
  useEffect(laden, [laden]);

  if (!f) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;
  const offen = f.status !== 'abgeschlossen' && f.status !== 'storniert';
  const rest = Math.max(0, f.betrag_cent - f.bezahlt_cent);

  async function los(pfad: string, body: unknown) {
    try {
      const r = await api<Forderung | { ok: boolean }>(pfad, { method: 'POST', body });
      if ('nummer' in r) setF(r);
      else laden();
      setDialog(null);
    } catch (e) {
      setFehler(errorText(e));
    }
  }

  const oeffne = (d: typeof dialog) => {
    setFehler('');
    setBeleg(f.mehrdata_beleg ?? '');
    setBetrag(rest ? (rest / 100).toFixed(2).replace('.', ',') : '');
    setArt('ueberweisung');
    setDatum(new Date().toLocaleDateString('sv-SE'));
    setText('');
    setDialog(d);
  };

  return (
    <>
      <VorgangKopf art="forderung" v={f} titel={`${formatGeld(f.betrag_cent)} – ${kundenName({ vorname: f.kunde_vorname, nachname: f.kunde_nachname, firma: f.kunde_firma })}`} />
      {fehler && !dialog && <Notice kind="error">{fehler}</Notice>}
      <div className="spalten haupt-links">
        <div className="stack">
          <Card title="Stand">
            <div className="stack" style={{ gap: 12 }}>
              <div className="grid3">
                <div>
                  <div className="small muted">Betrag</div>
                  <strong style={{ fontSize: 18 }}>{formatGeld(f.betrag_cent)}</strong>
                </div>
                <div>
                  <div className="small muted">Bezahlt</div>
                  <strong style={{ fontSize: 18 }}>{formatGeld(f.bezahlt_cent)}</strong>
                </div>
                <div>
                  <div className="small muted">Offen</div>
                  <strong style={{ fontSize: 18, color: rest ? 'var(--bad)' : 'var(--ok)' }}>{formatGeld(rest)}</strong>
                </div>
              </div>
              <div>
                <span className="small muted">MehrData-Beleg: </span>
                {f.mehrdata_beleg ? <span className="mono">{f.mehrdata_beleg}</span> : <Badge kind="warn">fehlt</Badge>}
              </div>
              {offen && (
                <div className="row">
                  {!f.mehrdata_beleg && (
                    <button className="primary" onClick={() => oeffne('beleg')}>
                      Beleg eintragen
                    </button>
                  )}
                  {rest > 0 && (
                    <button className={f.mehrdata_beleg ? 'primary' : ''} onClick={() => oeffne('zahlung')}>
                      Zahlung erfassen
                    </button>
                  )}
                  {f.mehrdata_beleg && (
                    <button className="klein" onClick={() => oeffne('beleg')}>
                      Beleg ändern
                    </button>
                  )}
                </div>
              )}
              <p className="small muted" style={{ margin: 0 }}>
                Erledigt ist die Forderung von selbst, sobald Beleg und Zahlungen in voller Höhe erfasst sind. Einen Knopf „bezahlt" gibt es bewusst nicht.
              </p>
            </div>
          </Card>
          <Card title="Weitere Schritte">
            <VorgangAktionen art="forderung" v={f} schritte={[]} onNeu={(v: Vorgang) => setF(v as Forderung)} offeneStatus={[{ wert: 'zahlung_offen', label: 'Zahlung offen' }]} />
          </Card>
          <Card title="Zahlungen" padded={false}>
            {(f.zahlungen ?? []).length === 0 ? (
              <Empty>Noch keine Zahlung.</Empty>
            ) : (
              <table>
                <tbody>
                  {f.zahlungen!.map((z) => (
                    <tr key={z.id} style={z.storniert_am ? { opacity: 0.55, textDecoration: 'line-through' } : undefined}>
                      <td>{formatDate(z.bezahlt_am)}</td>
                      <td>
                        {ZAHLART[z.art]}
                        {z.vor_ort && ' (vor Ort)'}
                        <div className="small muted">
                          erfasst von {z.erfasst_von}
                          {z.art === 'bar' && z.vor_ort && (z.bargeld_bestaetigt_am ? ` · im Laden bestätigt von ${z.bargeld_bestaetigt_von}` : ' · Bargeld noch nicht abgegeben')}
                        </div>
                        {z.storniert_am && <div className="small">storniert: {z.storno_grund}</div>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatGeld(z.betrag_cent)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {can('admin') && !z.storniert_am && (
                          <button className="klein gefahr" onClick={() => oeffne({ storno: z })}>
                            Stornieren
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          <VorgangVerlauf art="forderung" v={f} onNeu={(v) => setF(v as Forderung)} />
        </div>
        <Card title="Angaben">
          <div className="stack" style={{ gap: 12 }}>
            <KundeKasten v={f} />
            <dl className="daten">
              <dt>Herkunft</dt>
              <dd>
                {f.lieferung_id ? (
                  <Link to={`/lieferungen/${f.lieferung_id}`}>Lieferung {f.lieferung_nummer}</Link>
                ) : f.serviceauftrag_id ? (
                  <Link to={`/service/${f.serviceauftrag_id}`}>Serviceauftrag {f.serviceauftrag_nummer}</Link>
                ) : f.bestellung_id ? (
                  <Link to={`/bestellungen/${f.bestellung_id}`}>Bestellung {f.bestellung_nummer}</Link>
                ) : (
                  'von Hand angelegt'
                )}
                {f.einsatz_id && (
                  <div>
                    <Link to={`/einsaetze/${f.einsatz_id}`}>Einsatz {f.einsatz_nummer}</Link>
                  </div>
                )}
              </dd>
              <dt>Zuständig</dt>
              <dd>{f.zustaendig}</dd>
            </dl>
            <table>
              <tbody>
                {(f.positionen ?? []).map((p) => (
                  <tr key={p.id}>
                    <td className="small">
                      {p.menge !== 1 && `${p.menge} × `}
                      {p.bezeichnung}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatGeld(Math.round(p.menge * p.preis_cent))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {dialog === 'beleg' && (
        <Dialog
          titel="MehrData-Beleg eintragen"
          onClose={() => setDialog(null)}
          aktionen={
            <>
              <button onClick={() => setDialog(null)}>Abbrechen</button>
              <button className="primary" disabled={!beleg.trim()} onClick={() => void los(`/api/forderungen/${f.id}/beleg`, { beleg })}>
                Speichern
              </button>
            </>
          }
        >
          {fehler && <Notice kind="error">{fehler}</Notice>}
          <Field label="Rechnungs- bzw. Kassenbelegnummer aus MehrData">
            <input value={beleg} onChange={(e) => setBeleg(e.target.value)} autoFocus />
          </Field>
        </Dialog>
      )}
      {dialog === 'zahlung' && (
        <Dialog
          titel="Zahlung erfassen"
          onClose={() => setDialog(null)}
          aktionen={
            <>
              <button onClick={() => setDialog(null)}>Abbrechen</button>
              <button
                className="primary"
                disabled={!centAusText(betrag)}
                onClick={() =>
                  void los(`/api/forderungen/${f.id}/zahlung`, {
                    betragCent: centAusText(betrag),
                    art,
                    bezahltAm: datum ? new Date(`${datum}T12:00:00`).toISOString() : undefined,
                    notiz: text || null,
                  })
                }
              >
                Zahlung buchen
              </button>
            </>
          }
        >
          {fehler && <Notice kind="error">{fehler}</Notice>}
          <div className="grid3">
            <Field label="Betrag €" hint={`offen: ${formatGeld(rest)}`}>
              <input value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" autoFocus />
            </Field>
            <Field label="Art">
              <select value={art} onChange={(e) => setArt(e.target.value as Zahlung['art'])}>
                <option value="ueberweisung">Überweisung</option>
                <option value="ec">EC-Karte</option>
                <option value="bar">bar</option>
                <option value="sonstiges">sonstige</option>
              </select>
            </Field>
            <Field label="Bezahlt am">
              <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
            </Field>
          </div>
          <Field label="Notiz (z. B. Verwendungszweck)">
            <input value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        </Dialog>
      )}
      {dialog && typeof dialog === 'object' && (
        <Dialog
          titel="Zahlung stornieren"
          onClose={() => setDialog(null)}
          aktionen={
            <>
              <button onClick={() => setDialog(null)}>Abbrechen</button>
              <button className="gefahr" disabled={text.trim().length < 3} onClick={() => void los(`/api/forderungen/zahlungen/${dialog.storno.id}/stornieren`, { grund: text })}>
                Stornieren
              </button>
            </>
          }
        >
          {fehler && <Notice kind="error">{fehler}</Notice>}
          <p style={{ margin: 0 }}>
            {formatGeld(dialog.storno.betrag_cent)} {ZAHLART[dialog.storno.art]} vom {formatDate(dialog.storno.bezahlt_am)}. Die Zahlung bleibt sichtbar, zählt aber nicht mehr.
          </p>
          <Field label="Grund (Pflicht)">
            <input value={text} onChange={(e) => setText(e.target.value)} autoFocus />
          </Field>
        </Dialog>
      )}
    </>
  );
}

interface BarZahlung {
  id: string;
  betrag_cent: number;
  bezahlt_am: string;
  mitarbeiter_id: string;
  mitarbeiter: string;
  forderung_id: string;
  forderung_nummer: string;
  kunde_vorname: string | null;
  kunde_nachname: string | null;
  kunde_firma: string | null;
}

/** Bargeld, das Fahrer/Monteure kassiert haben - bis der Laden den Empfang bestätigt. */
export function BargeldPage() {
  const { user } = useAuth();
  const [liste, setListe] = useState<BarZahlung[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');

  const laden = useCallback(() => {
    void api<BarZahlung[]>('/api/forderungen/bargeld').then(setListe);
  }, []);
  useEffect(laden, [laden]);

  const gruppen = new Map<string, BarZahlung[]>();
  for (const z of liste ?? []) gruppen.set(z.mitarbeiter_id, [...(gruppen.get(z.mitarbeiter_id) ?? []), z]);

  async function bestaetigen(ids: string[], name: string, summe: number) {
    setFehler('');
    try {
      await api('/api/forderungen/bargeld/bestaetigen', { method: 'POST', body: { zahlungIds: ids } });
      setMeldung(`${formatGeld(summe)} von ${name} als erhalten bestätigt.`);
      laden();
    } catch (e) {
      setFehler(errorText(e));
    }
  }

  return (
    <>
      <div className="kopf">
        <h1>Bargeldabgabe</h1>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Bargeld, das vor Ort kassiert wurde. Erst wenn der Laden das Geld gezählt und bestätigt hat, verschwindet es von dieser Liste. Eigenes Bargeld
        bestätigt jeweils ein Kollege.
      </p>
      {fehler && <Notice kind="error">{fehler}</Notice>}
      {meldung && <Notice kind="success">{meldung}</Notice>}
      {!liste ? (
        <Loading />
      ) : liste.length === 0 ? (
        <Empty>Kein offenes Bargeld. 👍</Empty>
      ) : (
        <div className="stack">
          {[...gruppen.values()].map((zs) => {
            const summe = zs.reduce((s, z) => s + z.betrag_cent, 0);
            const name = zs[0]!.mitarbeiter;
            return (
              <Card
                key={zs[0]!.mitarbeiter_id}
                title={`${name} – ${formatGeld(summe)}`}
                actions={
                  zs[0]!.mitarbeiter_id !== user?.id ? (
                    <button className="ok" onClick={() => void bestaetigen(zs.map((z) => z.id), name, summe)}>
                      {formatGeld(summe)} erhalten
                    </button>
                  ) : (
                    <span className="small muted">eigenes Bargeld – ein Kollege bestätigt</span>
                  )
                }
                padded={false}
              >
                <table>
                  <tbody>
                    {zs.map((z) => (
                      <tr key={z.id}>
                        <td>{formatDateTime(z.bezahlt_am)}</td>
                        <td>{kundenName({ vorname: z.kunde_vorname, nachname: z.kunde_nachname, firma: z.kunde_firma })}</td>
                        <td>
                          <Link to={`/forderungen/${z.forderung_id}`}>{z.forderung_nummer}</Link>
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatGeld(z.betrag_cent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
